import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Method } from '@core/enums/api.enum';
import { IApiRequest } from '@core/interfaces/api-request';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const cache: { [key: string]: { time: Date, value: any } } = {};

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private _baseUrl = "";

  private _baseHttpHeaders = {

  };

  constructor(private _http: HttpClient) {
    this._baseUrl = environment.APIBaseUrl;
  }

  request<T>(options: IApiRequest): Observable<T> {
    switch (options.method) {
      case Method.GET:
        return this._http
          .get<T>(`${this._baseUrl}${options.relativeUrl}`, {
            headers: this.getHeaders(options.headers),
            params: this.getParams(options.parameters),
            withCredentials: true
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.POST:
        return this._http
          .post<T>(`${this._baseUrl}${options.relativeUrl}`, options.body, {
            headers: this.getHeaders(options.headers),
            withCredentials: true
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.DELETE:
        return this._http
          .request<T>(Method.DELETE, `${this._baseUrl}${options.relativeUrl}`, {
            headers: this.getHeaders(options.headers),
            params: this.getParams(options.parameters),
            withCredentials: true
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.PUT:
        return this._http
          .put<T>(`${this._baseUrl}${options.relativeUrl}`, options.body, {
            headers: this.getHeaders(options.headers),
            withCredentials: true
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
      case Method.PATCH:
        return this._http
          .patch<T>(`${this._baseUrl}${options.relativeUrl}`, options.body, {
            headers: this.getHeaders(options.headers),
            withCredentials: true
          })
          .pipe(
            map((result) => result),
            catchError(this.handleError)
          );
    }
  }

  public requestCached<T>(options: IApiRequest, ttl = 60000): Observable<T> {
    const key = JSON.stringify(options);
    const now = new Date();
    if (cache[key] && now.getTime() - cache[key].time.getTime() < ttl) {
      return of(cache[key].value);
    }

    return this.request<T>(options).pipe(
      tap((res) => {
        cache[key] = { time: new Date(), value: res };
      })
    );
  }

  private getHeaders(headers: { [key: string]: string }): HttpHeaders {
    if (headers)
      return new HttpHeaders({ ...this._baseHttpHeaders, ...headers });
    else return new HttpHeaders({ ...this._baseHttpHeaders });
  }

  private getParams(params: { [key: string]: string }): HttpParams {
    let httpParams = new HttpParams();
    for (let param in params) {
      if(params[param] != null && params[param] != '') {
        httpParams = httpParams.append(param, params[param]);
      }
    }
    return httpParams;
  }

  private handleError(errorRes: HttpErrorResponse) {
    return throwError(() => errorRes);
  }
}
